import React, { useCallback, useMemo, useRef } from 'react';
import { observer } from 'mobx-react';

import { translate } from 'services/i18nService';
import ModalService from 'services/ModalService';
import GiftsStore from 'stores/GiftsStore';
import ProfileStore from 'stores/ProfileStore';
import UserStore from 'stores/UserStore';
import Button from 'components/Button/Button';
import ContentCard from 'components/ContentCard/ContentCard';
import { SECOND_AS_MILLISECONDS } from 'enum/timeFormat';
import { isMyProfileHelperFunc } from 'utils/helpers';

import AddNewGift from './AddNewGift';
import GiftShowcaseItem from './GiftShowcaseItem';
import styles from './GiftShowcase.css';


const GiftShowcase = () => {
  const { user } = UserStore;
  const { profileName } = ProfileStore;
  const { hasMoreThanLimit } = GiftsStore;

  const giftsRef = useRef();

  const onGoBack = useCallback(() => {
    setTimeout(() => {
      giftsRef.current.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  }, []);

  const onEmptyGiftBackClick = useCallback(() => {
    setTimeout(() => {
      giftsRef.current.scrollIntoView({ behavior: 'smooth' });
    }, SECOND_AS_MILLISECONDS);
  }, []);

  const onSendGift = useCallback(() => {
    onGoBack();
    ModalService.showModal(
      ModalService.MODAL_KEYS.SEND_QUICK_GIFT_MODAL,
      { showClose: false }
    );
  }, [onGoBack]);

  const onSeeAll = useCallback(() => {
    GiftsStore.getGiftList(profileName, { size: 200 });

    ModalService.showModal(
      ModalService.MODAL_KEYS.ALL_GIFTS_MODAL,
      {
        close: ModalService.hideModal,
        show: true,
        isDesktop: true,
      }
    );
  }, [profileName]);

  const footer = useMemo(() => (
    <div className={styles.footerActions}>
      {!isMyProfileHelperFunc(user, profileName) && (
        <Button
          colour="orange"
          label={translate('v2profile.profile.send.gift')}
          onClick={onSendGift}
        />
      )}
      {hasMoreThanLimit && (
        <Button
          label={translate('v2profile.see.all.gifts')}
          onClick={onSeeAll}
        />
      )}
    </div>
  ), [
    hasMoreThanLimit,
    profileName,
    user,
    onSeeAll,
    onSendGift,
  ]);

  const renderGift = useCallback((item, i) => {
    if (item === 0) {
      return (
        <AddNewGift
          key={`giftAddNew-${i}`}
          handleBackClick={onGoBack}
          handleEmptyBackClick={onEmptyGiftBackClick}
        />
      );
    }

    return (
      <GiftShowcaseItem
        key={`giftItemShowcase-${i}`}
        gift={item}
        handleBackClick={onGoBack}
      />
    );
  }, [onEmptyGiftBackClick, onGoBack]);

  return (
    <ContentCard
      contentClass={styles.GiftShowcase}
      footer={footer}
      footerClass={styles.footerGiftShowcase}
      title={translate('v2profile.gifts.showCase.title')}
    >
      <div ref={giftsRef} className={styles.GiftShowcase}>
        {GiftsStore.sortedGiftsListDesktop.map(renderGift)}
      </div>
    </ContentCard>
  );
};

export default observer(GiftShowcase);
